// Module 10 — the wall applier: turns SceneSpec.walls (10.6 encoder output) into real Cesium geometry.
// Each neighbourhood parcel draws as a HOLLOW extruded WallGeometry band along its areaPolygon ring —
// luminous walls with an open interior, NOT a filled polygon (§5 of the vision). Geometry is STATIC
// (built once: ring + extrude height never change), so per-tick service status only mutates the
// per-instance COLOUR in place via getGeometryInstanceAttributes — no geometry rebuild, no FPS cliff
// (viz rule: primitives + in-place attribute mutation). Boundary: same-module + domain only.
import type { WallSpec } from "./effects/spec";
import type { CesiumLike, SceneLike, PrimitiveLike, GeometryInstanceLike, ColorLike } from "./cesium-api";

// Display heights sized for the boot camera (~150 km viewport). FIX: raised from 600→1200 m so the
// wall band is clearly visible (≈8 px) at the full-GTA boot zoom, making the per-status color readable
// at oblique angles. The footprint floor (footprints.ts) carries the top-down color; this band carries
// the 3D extruded wall glow. Display only — never the solver.
const WALL_BASE_M = 1200; // base extrude height of every parcel wall (was 600 — subpixel at boot zoom)
const WALL_CRIT_BONUS_M = 600; // highest-priority (criticalityPriority===1) loads stand taller

export interface WallStyle {
  hex: string;
  alpha: number;
}

// Kind hue is the parcel's identity; service status modulates it — each state reads apart by BOTH a
// distinct hue/value AND opacity, so colour is never the sole channel (the encoder also carries
// motion/hatch/dim/outage flags for later refinement). "dark" = blackout → grey, near-transparent.
// FIX: shed now uses a distinct RED hue (not the base color) so it's unmistakably visible on the map
// even when the footprint floor pins alpha. dark uses a darker grey. Both are Okabe-Ito CVD-safe.
export function wallStyle(w: WallSpec): WallStyle {
  // Alphas lowered (user feedback: neighbourhood parcels read as a solid, opaque mosaic that hid the
  // basemap/street grid underneath) so `normal` parcels are genuinely translucent; the escalating
  // stressed/shed/dark states keep MORE opacity than normal so a real condition still pops through.
  switch (w.status) {
    case "normal":
      return { hex: w.baseColor, alpha: 0.28 }; // transparent — basemap reads through
    case "stressed":
      return { hex: "#F0E442", alpha: 0.42 }; // brownout-risk — yellow, semi-transparent
    case "shed":
      return { hex: "#D55E00", alpha: 0.55 }; // deliberately curtailed → vermillion, more visible
    case "dark":
      return { hex: "#1a1a1a", alpha: 0.22 }; // de-energized → near-black, dim
    default:
      return { hex: w.baseColor, alpha: 0.28 }; // unknown
  }
}

export interface WallLayer {
  apply(walls: readonly WallSpec[]): void;
  setShow(visible: boolean): void; // perf-diagnosis lever (primitive.show — no rebuild)
  setModelMatrix(m: unknown): void; // 3D "float" lift — Matrix4 applied to the whole primitive
}

export function makeWallLayer(cesium: CesiumLike, scene: SceneLike): WallLayer {
  let primitive: PrimitiveLike | null = null;
  let liftMatrix: unknown = null; // pending 3D lift if set before the primitive is built
  // identity-stable pickable ids — clicking a neighbourhood wall selects its load node
  const idOf = new Map<string, { kind: "node"; id: string }>();
  const colorOf = (w: WallSpec): ColorLike => {
    const s = wallStyle(w);
    return cesium.Color.fromCssColorString(s.hex).withAlpha(s.alpha);
  };

  return {
    apply(walls): void {
      if (walls.length === 0) return; // no parcel geometry → nothing to draw (not an error)
      if (!primitive) {
        const instances: GeometryInstanceLike[] = walls.map((w) => {
          const flat: number[] = [];
          for (const c of w.ring) flat.push(c.lon, c.lat);
          const positions = cesium.Cartesian3.fromDegreesArray(flat);
          const top = WALL_BASE_M + (w.criticalCap ? WALL_CRIT_BONUS_M : 0);
          const geometry = new cesium.WallGeometry({
            positions,
            maximumHeights: w.ring.map(() => top), // per-vertex top → a flat-topped hollow band
            minimumHeights: w.ring.map(() => 0),
          });
          const pickId = { kind: "node" as const, id: w.id };
          idOf.set(w.id, pickId);
          return new cesium.GeometryInstance({
            geometry,
            id: pickId, // pickable + identity-stable for the in-place recolour lookup
            attributes: { color: cesium.ColorGeometryInstanceAttribute.fromColor(colorOf(w)) },
          });
        });
        primitive = new cesium.Primitive({
          geometryInstances: instances,
          appearance: new cesium.PerInstanceColorAppearance({ flat: true, translucent: true }),
          // asynchronous:true (Cesium default) — build the ~449 WallGeometry instances OFF the main
          // thread in Cesium's worker pool, spread across frames. asynchronous:false compiled them all
          // in ONE synchronous render pass (~3 s main-thread block ≈ the freeze). `primitive.ready` stays
          // false until the off-thread build finishes; the recolour branch below already defers on it.
          asynchronous: true,
        });
        if (liftMatrix != null) primitive.modelMatrix = liftMatrix; // apply a lift set before build
        scene.primitives.add(primitive);
        return;
      }
      // already built — recolour in place from the new per-tick status (no geometry rebuild).
      // Skip until the primitive has rendered once: Cesium refuses getGeometryInstanceAttributes
      // before its first update pass (a hidden tab pauses rAF while the worker keeps posting
      // snapshots). The build-time colour holds; the next rendered frame's snapshot recolours.
      if (!primitive.ready) return;
      for (const w of walls) {
        const pickId = idOf.get(w.id);
        if (!pickId) continue;
        const attr = primitive.getGeometryInstanceAttributes(pickId);
        attr.color = cesium.ColorGeometryInstanceAttribute.toValue(colorOf(w), attr.color); // real Cesium API: toValue(color, result)
      }
    },
    setShow(visible): void {
      if (primitive) primitive.show = visible;
    },
    setModelMatrix(m): void {
      liftMatrix = m;
      if (primitive) primitive.modelMatrix = m;
    },
  };
}
